<template>
  <CardLayout
    v-model="inventorySearch"
    title="Available Device Inventory"
    header-type="search"
  >
    <v-simple-table>
      <thead>
        <tr>
          <th>Device #</th>
          <th>Device Health</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody v-if="devicesLoading">
        <tr>
          <td colspan="3">
            <em>Loading devices from inventory...</em>
          </td>
        </tr>
      </tbody>
      <tbody v-else-if="organizationDevices.length > 0">
        <tr v-for="device in organizationDevices" :key="device.id">
          <td style="width: 40%; white-space: nowrap">
            <div class="d-flex align-center">
              {{ device.shortId || device.externalId }}
            </div>

            <v-badge
              color="orange"
              content="Refresh"
              v-if="device.labels?.isRefresh"
              class="align-center"
              inline
            ></v-badge>
          </td>
          <td style="width: 40%; white-space: nowrap">
            <DeviceGaugeCluster :device="device" />
          </td>
          <td style="white-space: nowrap">
            <v-btn
              :disabled="device.status !== 'ok'"
              depressed
              small
              tile
              color="success"
              @click="addDevice(device)"
              >Assign Device</v-btn
            >
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr>
          <td colspan="3">
            <em>No devices are available</em>
          </td>
        </tr>
      </tbody>
    </v-simple-table>
    <DialogLayout
      v-model="openRefreshDeviceWarning"
      title="Device Refresh Alert"
    >
      <p>
        This device was for refresh purposes only. Assigning it to a new renter
        will incur an {{ deviceChargeAmount }}device charge for your facilities
        next billing cycle. Would you like to proceed with assigning this
        device?
      </p>
      <template #actions>
        <v-btn color="primary" text @click="openRefreshDeviceWarning = false">
          No
        </v-btn>
        <v-btn color="primary" text @click="doAddDevice(refreshDevice)">
          Yes
        </v-btn>
      </template>
    </DialogLayout>
  </CardLayout>
</template>

<script>
import DeviceGaugeCluster from "@atoms/DeviceGaugeCluster.vue";
import CardLayout from "@layout/CardLayout.vue";
import DialogLayout from "@layout/DialogLayout.vue";

import { addOrganizationDevices } from "../../graphql/organizations";
import { ORGANIZATION_DEVICES } from "./graphql";
import { organization } from "../../graphql/organizations/zone";
import useFacility from "../authentication/useFacility";

export default {
  name: "CardDeviceInventory",
  components: { CardLayout, DialogLayout, DeviceGaugeCluster },
  props: {
    zoneId: {
      type: String,
      required: true,
    },
    smartUnit: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { currentFacilityId, currentFacility } = useFacility();

    return { currentFacilityId, currentFacility };
  },
  data: () => ({
    organizationDevices: {},
    inventorySearch: "",
    refreshDevice: null,
    openRefreshDeviceWarning: false,
  }),
  computed: {
    devicesLoading() {
      return this.$apollo.queries.organizationDevices.loading;
    },
    smartZoneSubscriptionAmount() {
      return (
        this.currentFacility.value?.billingProfile
          ?.smartZoneSubscriptionAmount || "$12.00"
      );
    },
    deviceChargeAmount() {
      if (!this.currentFacility.billingProfile?.deviceChargeAmount) {
        return;
      }
      return `${this.currentFacility.billingProfile?.deviceChargeAmount} `;
    },
  },
  apollo: {
    organizationDevices: {
      query: ORGANIZATION_DEVICES,

      variables() {
        return {
          id: this.currentFacilityId,
          searchText: this.inventorySearch,
          status: "ok",
          dispositions: ["in_service"],
        };
      },
      update({ organizationDevices }) {
        return organizationDevices
          .filter((device) => !device.rmaRequest)
          .sort((a, b) =>
            (a.shortId || a.externalId) > (b.shortId || b.externalId) ? 1 : -1
          );
      },
    },
  },
  methods: {
    hasRmaDevice() {
      return (
        this.smartUnit?.billingAccounts?.some(
          (account) => account?.status !== "closed"
        ) && this.smartUnit?.devices.some((device) => device.rmaRequest)
      );
    },
    addDevice(device) {
      if (device.labels?.isRefresh && !this.hasRmaDevice()) {
        this.openRefreshDeviceWarning = true;
        this.refreshDevice = device;
      } else {
        this.refreshDevice = null;
        this.doAddDevice(device);
      }
    },

    async doAddDevice({ id }) {
      try {
        await this.$apollo.mutate({
          mutation: addOrganizationDevices,
          variables: {
            id: this.zoneId,
            devices: [id],
          },
          refetchQueries: this.zoneRefetchQueries,
        });
        this.openRefreshDeviceWarning = false;
        this.refreshDevice = null;
      } catch (error) {
        console.error(error);
      }
    },
    zoneRefetchQueries() {
      return [
        {
          query: organization,
          variables: { id: this.zoneId },
        },
        {
          query: ORGANIZATION_DEVICES,
          variables: {
            id: this.currentFacilityId,
            searchText: this.inventorySearch,
            status: "ok",
            dispositions: ["in_service"],
          },
          update({ organizationDevices }) {
            return organizationDevices.filter((device) => !device.rmaRequest);
          },
        },
      ];
    },
  },
};
</script>
